import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'

import languageIconUrl from '@/assets/profile-menu/language.svg'
import logoutIconUrl from '@/assets/profile-menu/logout.svg'
import profileIconUrl from '@/assets/profile-menu/profile.svg'
import type { SupportedLanguage } from '@/i18n/i18n'
import type { PortalKind } from '@/lib/auth/token-storage'
import { cn } from '@/lib/cn'

import { UserAvatar } from './user-avatar'
import { getDisplayName, getUserEmail, getUserInitial, type AppShellUser } from './user-display'

function ProfileMenuIcon({ className, src }: { className: string; src: string }) {
  return <img alt="" aria-hidden className={className} src={src} />
}

function UserMenuItem({
  active = false,
  children,
  icon,
  onClick,
  tone = 'default',
  trailing,
}: {
  active?: boolean
  children: ReactNode
  icon: ReactNode
  onClick?: () => void
  tone?: 'danger' | 'default'
  trailing?: ReactNode
}) {
  return (
    <button
      className={cn(
        'flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg px-3 text-left transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        tone === 'danger' ? 'text-[#d92d20]' : 'text-[var(--color-sidebar-text)]',
        active ? 'sidebar-active-soft' : 'bg-transparent',
      )}
      onClick={onClick}
      type="button"
    >
      <span className="flex size-5 shrink-0 items-center justify-center text-current">{icon}</span>
      <span className="min-w-0 flex-1 truncate text-sm">{children}</span>
      {trailing ? (
        <span className="shrink-0 text-[11px] text-[var(--color-sidebar-text-muted)]">
          {trailing}
        </span>
      ) : null}
    </button>
  )
}

function LanguageMenu({
  currentLanguage,
  onLanguageChange,
}: {
  currentLanguage: SupportedLanguage
  onLanguageChange: (language: SupportedLanguage) => void
}) {
  const { t } = useTranslation('shell')
  const options: { label: string; value: SupportedLanguage }[] = [
    { label: t('language.simplifiedChinese'), value: 'zh-Hans' },
    { label: t('language.english'), value: 'en-US' },
  ]

  return (
    <div
      className="fixed bottom-[133px] left-[248px] z-[70] w-[132px] rounded-xl border border-[var(--color-sidebar-border-subtle)] bg-white p-[5px] shadow-[0_0_10px_rgba(0,0,0,0.1)]"
      data-testid="profile-language-menu"
    >
      {options.map((option) => (
        <button
          aria-pressed={currentLanguage === option.value}
          className={cn(
            'h-8 w-full cursor-pointer rounded-md px-3 text-left text-[var(--color-sidebar-text)] transition-colors hover:bg-[var(--color-sidebar-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            currentLanguage === option.value ? 'sidebar-active-soft' : 'bg-transparent',
          )}
          key={option.value}
          onClick={() => onLanguageChange(option.value)}
          type="button"
        >
          <span className="text-sm">{option.label}</span>
        </button>
      ))}
    </div>
  )
}

interface ProfileMenuContentProps {
  currentLanguage: SupportedLanguage
  languageOpen: boolean
  onLanguageToggle: () => void
  onLogout: () => void
  onOpenProfile: () => void
  profileLabel: string
}

function ProfileMenuContent({
  currentLanguage,
  languageOpen,
  onLanguageToggle,
  onLogout,
  onOpenProfile,
  profileLabel,
}: ProfileMenuContentProps) {
  const { t } = useTranslation('shell')
  const languageLabel =
    currentLanguage === 'zh-Hans' ? t('language.simplifiedChinese') : t('language.english')

  return (
    <>
      <div className="grid gap-0 px-2 py-2">
        <UserMenuItem
          icon={<ProfileMenuIcon className="h-4 w-[14px]" src={profileIconUrl} />}
          onClick={onOpenProfile}
        >
          {profileLabel}
        </UserMenuItem>
      </div>

      <div className="mx-5 h-px bg-[var(--color-sidebar-border-subtle)]" />

      <div className="grid gap-0 px-2 py-2">
        <UserMenuItem
          active={languageOpen}
          icon={<ProfileMenuIcon className="size-[14px]" src={languageIconUrl} />}
          onClick={onLanguageToggle}
          trailing={languageLabel}
        >
          {t('userMenu.language')}
        </UserMenuItem>
      </div>

      <div className="mx-5 h-px bg-[var(--color-sidebar-border-subtle)]" />

      <div className="px-2 py-2">
        <UserMenuItem
          icon={<ProfileMenuIcon className="size-[14px]" src={logoutIconUrl} />}
          onClick={onLogout}
          tone="danger"
        >
          {t('userMenu.logout')}
        </UserMenuItem>
      </div>
    </>
  )
}

export function ProfileMenu({
  currentLanguage,
  onLanguageChange,
  onLogout,
  portal,
  user,
}: {
  currentLanguage: SupportedLanguage
  onLanguageChange: (language: SupportedLanguage) => void
  onLogout: () => void
  portal: PortalKind
  user: AppShellUser
}) {
  const { t } = useTranslation('shell')
  const navigate = useNavigate()
  const [languageOpen, setLanguageOpen] = useState(false)
  const displayName = getDisplayName(user, t('userMenu.fallbackName'))
  const initial = getUserInitial(user, t('userMenu.fallbackName'))
  const avatarSrc = user.avatar?.trim()
  const profileLabel = portal === 'admin' ? t('userMenu.adminProfile') : t('userMenu.mySettings')

  return (
    <>
      <div
        className="fixed bottom-[56px] left-0 z-[60] w-[248px] rounded-xl border border-[var(--color-sidebar-border-subtle)] bg-white shadow-[0_0_10px_rgba(26,28,25,0.1)]"
        data-profile-menu-portal={portal}
        data-testid="profile-menu"
      >
        <div className="flex h-[73px] items-center gap-3 px-6">
          <UserAvatar initial={initial} src={avatarSrc} />
          <div className="min-w-0">
            <div className="truncate text-base font-bold leading-5 text-[var(--color-sidebar-active-text)]">
              {displayName}
            </div>
            <div className="mt-1 truncate text-[11px] leading-4 text-[var(--color-sidebar-text-muted)]">
              {getUserEmail(user)}
            </div>
          </div>
        </div>

        <div className="h-px bg-[var(--color-sidebar-border-subtle)]" />

        <ProfileMenuContent
          currentLanguage={currentLanguage}
          languageOpen={languageOpen}
          onLanguageToggle={() => {
            setLanguageOpen((open) => !open)
          }}
          onLogout={onLogout}
          onOpenProfile={() => {
            void navigate({ to: portal === 'admin' ? '/admin/profile' : '/profile' })
          }}
          profileLabel={profileLabel}
        />
      </div>
      {languageOpen ? (
        <LanguageMenu currentLanguage={currentLanguage} onLanguageChange={onLanguageChange} />
      ) : null}
    </>
  )
}
